class Festival {
  /**
   * @param {Date} fromDate
   * @param {Date} toDate
   * @param {string} dateString
   * @param {string} name
   */
  constructor(fromDate, toDate, dateString, name) {
    /**
     * @type {number}
     * @readonly
     */
    this.fromTimestamp = fromDate.valueOf();
    /**
     * @type {number}
     * @readonly
     */
    this.toTimestamp = toDate.valueOf();
    /**
     * @type {string}
     * @readonly
     */
    this.dateString = dateString;
    /**
     * @type {string}
     * @readonly
     */
    this.name = name;
  }

  /**
   * @param {Date} date
   * @returns {boolean}
   */
  dateInPeriod(date) {
    const dateTimestamp = date.valueOf();

    return (
      dateTimestamp >= this.fromTimestamp && dateTimestamp <= this.toTimestamp
    );
  }

  /**
   * @param {number} year
   * @param {number} month
   * @returns {boolean}
   */
  inYearMonth(year, month) {
    const monthIndex = month - 1;
    const fromDate = new Date(this.fromTimestamp);
    const toDate = new Date(this.toTimestamp);

    return (
      (fromDate.getFullYear() === year && fromDate.getMonth() === monthIndex) ||
      (toDate.getFullYear() === year && toDate.getMonth() === monthIndex)
    );
  }
}

class Closure extends Festival {
  /**
   * @param {Date} fromDate
   * @param {Date} toDate
   * @param {string} dateString
   * @param {string} name
   * @param {string} allowedProductSubstring
   * @param {string} notAvailableMessage
   */
  constructor(
    fromDate,
    toDate,
    dateString,
    name,
    allowedProductSubstring,
    notAvailableMessage,
  ) {
    super(fromDate, toDate, dateString, name);

    /**
     * @type {string}
     * @readonly
     */
    this.allowedProductSubstring = allowedProductSubstring;

    /**
     * @type {string}
     * @readonly
     */
    this.notAvailableMessage = notAvailableMessage;
  }
}

/**
 * @param {string} dateString
 * @returns {Date}
 */
function stringToDate(dateString) {
  if (dateString.length === 0) {
    throw new Error("Invalid date string");
  }

  const components = dateString.split("-");

  if (components.length !== 3) {
    throw new Error("Invalid date string");
  }

  const year = Number.parseInt(components[0]);

  if (Number.isNaN(year)) {
    throw new Error("Invalid date string");
  }

  const month = Number.parseInt(components[1]);

  if (Number.isNaN(month)) {
    throw new Error("Invalid date string");
  }

  const day = Number.parseInt(components[2]);

  if (Number.isNaN(day)) {
    throw new Error("Invalid date string");
  }

  const date = new Date(year, month - 1, day);

  if (Number.isNaN(date.valueOf())) {
    throw new Error("Invalid date string");
  }

  return date;
}

/**
 * @param {string} scriptSelector
 * @param {Array<string>} expectedHeaderRow
 * @returns {Array<Array<string>>}
 */
function getCsv(scriptSelector, expectedHeaderRow) {
  const script = document.querySelector(scriptSelector);

  if (script === null || script.textContent === null) {
    throw new TypeError("Null");
  }

  if (script.textContent.trim() === "") {
    return [];
  }

  const data = script.textContent
    .split("\n")
    .filter((x) => x)
    .map((x) => x.split("|"));

  if (data.length === 0) {
    throw new Error("Empty CSV");
  }

  if (
    data[0].length !== expectedHeaderRow.length ||
    data[0].some((x, i) => x !== expectedHeaderRow[i])
  ) {
    throw new Error("Headers not expected");
  }

  return data;
}

class DateProcessor {
  constructor() {
    const legend = document.querySelector("#delivery-date__legend");

    if (legend === null) {
      throw new Error("Legend not found");
    }

    /**
     * @type {HTMLElement}
     * @readonly
     */
    this.legend = /** @type {HTMLElement} */ (legend);

    const festivalCsv = getCsv("[data-delivery-date-festivals-csv]", [
      "from_date",
      "to_date",
      "display_date",
      "name",
    ]);

    /**
     * @type {Array<Festival>}
     * @readonly
     */
    this.festivals = [];

    for (let i = 1; i < festivalCsv.length; ++i) {
      const row = festivalCsv[i];
      const fromDate = stringToDate(row[0]);
      const toDate = stringToDate(row[1]);
      const displayDate = row[2];
      const name = row[3];

      this.festivals.push(new Festival(fromDate, toDate, displayDate, name));
    }

    const closureCsv = getCsv("[data-delivery-date-closures-csv]", [
      "from_date",
      "to_date",
      "display_date",
      "name",
      "allowed_product_substring",
      "not_available_message",
    ]);

    /**
     * @type {Array<Closure>}
     * @readonly
     */
    this.closures = [];

    for (let i = 1; i < closureCsv.length; ++i) {
      const row = closureCsv[i];
      const fromDate = stringToDate(row[0]);
      const toDate = stringToDate(row[1]);
      const displayDate = row[2];
      const name = row[3];
      const allowedProductSubstring = row[4];
      const notAvailableMessage = row[5];

      this.closures.push(
        new Closure(
          fromDate,
          toDate,
          displayDate,
          name,
          allowedProductSubstring,
          notAvailableMessage,
        ),
      );
    }

    const productTitlesScript = document.querySelector(
      "[data-delivery-date-line-item-json]",
    );

    if (
      productTitlesScript === null ||
      productTitlesScript.textContent === null
    ) {
      throw new TypeError("Null");
    }

    /**
     * @type {Array<string>}
     * @readonly
     */
    this.productTitles = JSON.parse(productTitlesScript.textContent);

    const now = new Date();
    this.updateLegend(now.getFullYear(), now.getMonth() + 1);
  }

  /**
   * @param {Date} date
   * @returns {[boolean, string, string]}
   */
  processDate(date) {
    const festivals = this.festivals.filter((festival) =>
      festival.dateInPeriod(date),
    );
    const closures = this.closures.filter((closure) => {
      if (closure.dateInPeriod(date)) {
        if (closure.allowedProductSubstring) {
          const notAvailableProducts = this.productTitles.filter(
            (title) => !title.includes(closure.allowedProductSubstring),
          );

          return notAvailableProducts.length > 0;
        }

        return true;
      }

      return false;
    });
    const periods = festivals.concat(closures);
    const specialCount = festivals.length;
    const cautionCount = closures.length;
    const selectable = true;

    if (periods.length > 0) {
      return [
        selectable,
        `festival-code-${cautionCount}-${specialCount}`,
        periods.map((x) => x.name).join("\n"),
      ];
    }

    return [selectable, "", ""];
  }

  /**
   * @param {number} year
   * @param {number} month
   */
  updateLegend(year, month) {
    this.legend.innerHTML = this.festivals
      .concat(this.closures)
      .filter((period) => period.inYearMonth(year, month))
      .filter((period) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return period.fromTimestamp >= today.valueOf();
      })
      .sort((a, b) => a.fromTimestamp - b.fromTimestamp)
      .map((period) => {
        let notAvailableLine = "";

        if (period.allowedProductSubstring) {
          const notAvailableProducts = this.productTitles.filter(
            (title) => !title.includes(period.allowedProductSubstring),
          );

          if (notAvailableProducts.length === 0) {
            return "";
          }

          notAvailableLine = period.notAvailableMessage.replace(
            "[product_list]",
            `<ul>${notAvailableProducts.map((x) => `<li>${x}</li>`).join("")}</ul>`,
          );
        }

        return `
          <li class="delivery-date__legend-item">
            <div class="delivery-date__legend-item-icon" style="font-size:75%;text-align: center;padding-top: 4px;">${period instanceof Closure ? "🔴" : "🟢"}</div>
            <div class="delivery-date__legend-item-date">${period.dateString}</div>
            <div class="delivery-date__legend-item-description">${period.name}</div>
            <div class="delivery-date__legend-item-not-available">${notAvailableLine}</div>
          </li>
        `;
      })
      .join("");

    if (this.legend.children.length > 0) {
      this.legend.classList.remove("delivery-date__legend--hidden");
    } else {
      this.legend.classList.add("delivery-date__legend--hidden");
    }
  }
}

window.DateProcessor = DateProcessor;
